/**
 * Atualizacao segura — `PWA §5.2`, PWA-010 a PWA-013.
 *
 * O nucleo e uma **maquina de estados pura**, sem `navigator` e sem React, porque o requisito
 * que mais importa aqui e negativo: *"enquanto `checkoutInProgress`, `pixPollingActive` ou
 * `uploadInProgress` forem verdadeiros, o aviso nao e exibido"* (PWA-011). Requisito negativo
 * so e verificavel se o estado for inspecionavel — e um `useEffect` que chama `setState` nao e.
 *
 * ## O que pode dar errado, e por que a regra e essa
 *
 * `skipWaiting()` no `install` ativa o service worker novo enquanto a pagina antiga roda. O
 * bundle ja carregado continua pedindo `route-checkout-<hash-antigo>.js`, que o SW novo nao tem
 * no precache e que o deploy ja removeu do CDN. O sintoma em producao e `ChunkLoadError`, e ele
 * aparece **no clique de "Pagar"** — porque e o ultimo chunk a ser pedido. Dai a regra
 * PWA-010: `skipWaiting` so por mensagem, e a mensagem so sai de um toque do usuario.
 */

/** Os tres fluxos que suprimem o aviso (PWA-011). */
export interface FluxosCriticos {
  readonly checkoutInProgress: boolean;
  readonly pixPollingActive: boolean;
  readonly uploadInProgress: boolean;
}

export const NENHUM_FLUXO_CRITICO: FluxosCriticos = {
  checkoutInProgress: false,
  pixPollingActive: false,
  uploadInProgress: false,
};

export type EstadoDaAtualizacao =
  /** Nao ha versao nova esperando. */
  | { readonly tipo: 'OCIOSO' }
  /** Ha versao nova, mas o usuario esta em fluxo critico: o aviso fica pendente. */
  | { readonly tipo: 'PENDENTE'; readonly motivo: keyof FluxosCriticos }
  /** Ha versao nova e o aviso pode aparecer. */
  | { readonly tipo: 'DISPONIVEL' }
  /** O usuario aceitou; esperando `controllerchange` para recarregar. */
  | { readonly tipo: 'APLICANDO' };

export interface EntradaDaMaquina {
  /** Ha um service worker em `waiting`? */
  readonly temVersaoNova: boolean;
  readonly fluxos: FluxosCriticos;
  /** O usuario tocou em "Atualizar". */
  readonly aceito: boolean;
}

export function emFluxoCritico(fluxos: FluxosCriticos): keyof FluxosCriticos | null {
  if (fluxos.checkoutInProgress) return 'checkoutInProgress';
  if (fluxos.pixPollingActive) return 'pixPollingActive';
  if (fluxos.uploadInProgress) return 'uploadInProgress';
  return null;
}

/**
 * A decisao, inteira, em cinco linhas.
 *
 * PWA-012 esta implicito e e deliberado: nao existe estado "MODAL". Se o usuario ignorar o
 * aviso, ele **nao** escala — a versao nova entra sozinha no proximo *cold start*, quando nao
 * ha cliente controlado e o SW novo ativa naturalmente. Isso cobre 90% dos casos sem incomodar
 * ninguem.
 */
export function proximoEstado(entrada: EntradaDaMaquina): EstadoDaAtualizacao {
  if (!entrada.temVersaoNova) return { tipo: 'OCIOSO' };
  if (entrada.aceito) return { tipo: 'APLICANDO' };
  const motivo = emFluxoCritico(entrada.fluxos);
  if (motivo !== null) return { tipo: 'PENDENTE', motivo };
  return { tipo: 'DISPONIVEL' };
}

/**
 * PWA-013 — `registration.update()` com *throttle* de 60 min por sessao.
 *
 * "Nao a cada navegacao — em SPA, navegacao e troca de rota, e chamar `update()` toda vez custa
 * uma requisicao por clique." Num publico que conta MB, uma requisicao por clique e franquia.
 */
export const INTERVALO_MINIMO_DE_VERIFICACAO_MS = 60 * 60 * 1000;

export interface AceleradorDeVerificacao {
  /** `true` quando ja passou o intervalo; registra o instante e devolve `true` uma unica vez. */
  podeVerificar: (agoraMs: number) => boolean;
}

export function criarAceleradorDeVerificacao(
  intervaloMs: number = INTERVALO_MINIMO_DE_VERIFICACAO_MS,
): AceleradorDeVerificacao {
  let ultima = Number.NEGATIVE_INFINITY;
  return {
    podeVerificar(agoraMs) {
      if (agoraMs - ultima < intervaloMs) return false;
      ultima = agoraMs;
      return true;
    },
  };
}
